import { DealerGroup } from '../types/Group';

const API_URL = process.env.REACT_APP_API_URL || '/api';

export const GroupService = {
  // Get all groups
  getAllGroups: async (): Promise<DealerGroup[]> => {
    try {
      const response = await fetch(`${API_URL}/groups`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...(localStorage.getItem('token') && { 'Authorization': `Bearer ${localStorage.getItem('token')}` })
        },
        mode: 'cors'
      });
      
      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Failed to fetch dealer groups:', error);
      throw error;
    }
  },
  
  // Get a single group by ID
  getGroupById: async (id: number): Promise<DealerGroup> => {
    try {
      const response = await fetch(`${API_URL}/groups/id/${id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...(localStorage.getItem('token') && { 'Authorization': `Bearer ${localStorage.getItem('token')}` })
        },
        mode: 'cors'
      });
      
      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error(`Failed to fetch group with ID ${id}:`, error);
      throw error;
    }
  },

  // Get groups by dealer code
  getGroupsByDealerCode: async (dealerCode: string): Promise<DealerGroup[]> => {
    try {
      const response = await fetch(`${API_URL}/groups/dealer/${dealerCode}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...(localStorage.getItem('token') && { 'Authorization': `Bearer ${localStorage.getItem('token')}` })
        },
        mode: 'cors'
      });
      
      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error(`Failed to fetch groups for dealer ${dealerCode}:`, error);
      throw error;
    }
  },

  // Get groups by group name
  getGroupsByName: async (groupName: string): Promise<DealerGroup[]> => {
    try {
      const response = await fetch(`${API_URL}/groups/name/${encodeURIComponent(groupName)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...(localStorage.getItem('token') && { 'Authorization': `Bearer ${localStorage.getItem('token')}` })
        },
        mode: 'cors'
      });
      
      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error(`Failed to fetch groups with name ${groupName}:`, error);
      throw error;
    }
  },

  // Get unique group names
  getUniqueGroupNames: async (): Promise<{groupName: string}[]> => {
    try {
      const response = await fetch(`${API_URL}/groups/names`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...(localStorage.getItem('token') && { 'Authorization': `Bearer ${localStorage.getItem('token')}` })
        },
        mode: 'cors'
      });
      
      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Failed to fetch unique group names:', error);
      throw error;
    }
  },
  
  // Create a new group
  createGroup: async (group: DealerGroup): Promise<DealerGroup> => {
    try {
      console.log('DEBUG: GroupService.createGroup called with:', group);
      console.log('DEBUG: API_URL:', API_URL);
      console.log('DEBUG: Full URL:', `${API_URL}/groups`);
      
      const requestBody = JSON.stringify(group);
      console.log('DEBUG: Request body:', requestBody);
      
      const response = await fetch(`${API_URL}/groups`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...(localStorage.getItem('token') && { 'Authorization': `Bearer ${localStorage.getItem('token')}` })
        },
        mode: 'cors',
        body: requestBody
      });
      
      console.log('DEBUG: Response status:', response.status);
      console.log('DEBUG: Response headers:', response.headers);
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error('DEBUG: Error response data:', errorData);
        throw new Error(errorData.message || `Error ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('DEBUG: Success response data:', data);
      return data.data;
    } catch (error) {
      console.error('Failed to create dealer group:', error);
      throw error;
    }
  },
  
  // Update an existing group
  updateGroup: async (id: number, group: DealerGroup): Promise<DealerGroup> => {
    try {
      const response = await fetch(`${API_URL}/groups/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...(localStorage.getItem('token') && { 'Authorization': `Bearer ${localStorage.getItem('token')}` })
        },
        mode: 'cors',
        body: JSON.stringify(group)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Error ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error(`Failed to update group with ID ${id}:`, error);
      throw error;
    }
  },
  
  // Delete a group
  deleteGroup: async (id: number): Promise<void> => {
    try {
      const response = await fetch(`${API_URL}/groups/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...(localStorage.getItem('token') && { 'Authorization': `Bearer ${localStorage.getItem('token')}` })
        },
        mode: 'cors'
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Error ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      console.error(`Failed to delete group with ID ${id}:`, error);
      throw error;
    }
  },

  // Delete all entries for a dealer
  deleteDealerFromGroups: async (dealerCode: string): Promise<void> => {
    try {
      const response = await fetch(`${API_URL}/groups/dealer/${dealerCode}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...(localStorage.getItem('token') && { 'Authorization': `Bearer ${localStorage.getItem('token')}` })
        },
        mode: 'cors'
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Error ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      console.error(`Failed to delete dealer ${dealerCode} from groups:`, error);
      throw error;
    }
  },

  // Delete all entries for a group name
  deleteGroupByName: async (groupName: string): Promise<void> => {
    try {
      const response = await fetch(`${API_URL}/groups/name/${encodeURIComponent(groupName)}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...(localStorage.getItem('token') && { 'Authorization': `Bearer ${localStorage.getItem('token')}` })
        },
        mode: 'cors'
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Error ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      console.error(`Failed to delete group ${groupName}:`, error);
      throw error;
    }
  }
};

export default GroupService; 